import React, { useState, useEffect, useMemo, useCallback } from "react";
import Header from "./Header";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationModal from "../Components/Utils/ConfirmationModal";
import auth_guid from "../auth_guid.json";
import Metroline from "../Components/Ui/Metroline";
import { ProjectApi } from "../Api/ProjectApi";
import SortableHeader from "../Components/Table/sortableHeader";
import { AdminApi } from "../Api/AdminApi";
import QuestionnaireFilter from "../Components/Filters/QuestionnaireFilter";
import { Option } from "../Components/InputComponents/Filters/MultiSelect";

interface Questionnaire {
  id: string;
  name: string;
  projectCode: string;
  isCBE: boolean;
  projectOfficer: string;
  projectCoordinator : string;
  userLockingEmail?: string;
  lastModifiedDate_User : string;
  userRole: number;
  status: number;
}

interface QuestionnaireFilters {
  names: string[];
  types: string[];
  projectNumbers: string[];
  projectOfficers: string[];
}

interface SortConfig {
  key: keyof Questionnaire | null;
  direction: 'ascending' | 'descending' | null;
}

function ListOfQuestionnaires() {
  // Modals states
  const [modalRegressIsOpen, setModalRegressIsOpen] = useState<boolean>(false);
  const [modalProgressIsOpen, setModalProgressIsOpen] = useState<boolean>(false);
  const [modalCloneIsOpen, setModalCloneIsOpen] = useState<boolean>(false);
  const [selectedSelectedProjectId, setSelectedSelectedProjectId] = useState<string>("");
  const [regressStatusTo, setRegressStatusTo] = useState<number | null>(null);

  // New state for selected questionnaires
  const [selectedQuestionnaireList, setSelectedQuestionnaireList] = useState<string[]>([]);

  // Configuration and authentication
  const scopeUrl =
    process.env.NODE_ENV === "production"
      ? "api://" + auth_guid.prod.tenantId + "/User.Read"
      : "https://CBEDevelopWebappTest.onmicrosoft.com/" + auth_guid.local.tenantId + "/User.Read";

  const config =
    process.env.NODE_ENV === "production"
      ? require("../Config.prod").default
      : require("../Config.local").default;

  const { instance, accounts } = useMsal();
  const [userToken, setuserToken] = useState<string | null>();
  const navigate = useNavigate();
  
  // Application states
  const [rawData, setRawData] = useState<Questionnaire[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const api = new ProjectApi(accounts[0], instance);
  const adminApi = new AdminApi(accounts[0], instance);
  
  // Filtering by tab (status) via Metroline
  const { statusId } = useParams<{ statusId?: string }>();
  const [selectedStatus, setSelectedStatus] = useState<number | null>(() => {
    if (statusId === undefined) return 1;
    const parsed = parseInt(statusId);
    return isNaN(parsed) ? 1 : parsed;
  });

  // Advanced filtering
  const [filters, setFilters] = useState<QuestionnaireFilters>({
    names: [],
    types: [],
    projectNumbers: [],
    projectOfficers: []
  });

  // Options for filters
  const [nameOptions, setNameOptions] = useState<Option[]>([]);
  const [typeOptions, setTypeOptions] = useState<Option[]>([]);
  const [projectNumberOptions, setProjectNumberOptions] = useState<Option[]>([]);
  const [projectOfficerOptions, setProjectOfficerOptions] = useState<Option[]>([]);
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null
  });

  // Handler for selecting/deselecting questionnaires
  const handleSelectQuestionnaire = (questionnaireId: string, isChecked: boolean) => {
    if (isChecked) {
      // Add ID to the list if checkbox is checked
      setSelectedQuestionnaireList(prevList => [...prevList, questionnaireId]);
    } else {
      // Remove ID from the list if checkbox is unchecked
      setSelectedQuestionnaireList(prevList => 
        prevList.filter(id => id !== questionnaireId)
      );
    }
  };

  // Fetch data from API
  const fetchData = async () => {
    const response = await instance.acquireTokenSilent({
      scopes: [scopeUrl],
      account: accounts[0],
    });
    const axiosConfig = {
      headers: {
        Authorization: `Bearer ${response.accessToken}`,
      },
    };
    setuserToken(response.accessToken);
    
    try {
      const response = await axios.get(config.apiUrl + "/Project/GetAllActiveProjects", axiosConfig);
      const questionnaires = response.data.questionnaires;
      setRawData(questionnaires);
      setIsAdmin(response.data.isAdmin);
      console.log('admin setted to ' ,response.data.isAdmin)
      setDataLoaded(true);
      
      // Les options de filtres seront générées après en fonction du statut sélectionné
      // dans useEffect qui dépend de filteredByStatus
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  useEffect(() => {
    if (dataLoaded) {
      const now = new Date();
      const delay = (5 - (now.getMinutes() % 5)) * 60 * 1000 - now.getSeconds() * 1000 + 10000;

      const timeout = setTimeout(() => {
        fetchData();
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        return () => clearInterval(interval);
      }, delay);

      return () => clearTimeout(timeout);
    }
  }, [dataLoaded]);

  // Calculate status counters for Metroline
  const [statusCounts, setStatusCounts] = useState<number[]>(Array(5).fill(0));

  useEffect(() => {
    const counts = Array(5).fill(0);
    rawData.forEach((project) => {
      counts[project.status]++;
    });
    setStatusCounts(counts);
  }, [rawData]);
  

  const hasHighLevelRole = useMemo(() => {
    return rawData.some(project => project.userRole === 3 || project.userRole === 4);
  }, [rawData]);

  // Function to handle filters modified by the QuestionnaireFilter component
  const handleFiltersChange = (newFilters: QuestionnaireFilters) => {
    setFilters(newFilters);
  };

  // *** SIMPLIFIED SECTION: Filtering + Sorting in a single data source ***
  
  // 1. Status filtering (Metroline)
  const filteredByStatus = useMemo(() => {
    if (selectedStatus === null || !hasHighLevelRole) {
      return rawData;
    }
    return rawData.filter(project => project.status === selectedStatus);
  }, [rawData, selectedStatus, hasHighLevelRole]);
  
  // Génère les options de filtres basées sur le statut sélectionné
  useEffect(() => {
    // Utilise les projets déjà filtrés par statut (filteredByStatus)
    const projectsForCurrentStatus = filteredByStatus;
    
    // Name options
    const names: Option[] = projectsForCurrentStatus.map((q: Questionnaire) => ({
      value: q.id,
      label: q.name
    }));
    
    // Type options (only those present in projects)
    const uniqueTypes = new Set<boolean>();
    projectsForCurrentStatus.forEach((q: Questionnaire) => uniqueTypes.add(q.isCBE));
    
    const types: Option[] = Array.from(uniqueTypes).map(isCBE => ({
      value: isCBE.toString(),
      label: isCBE ? "CBE" : "BBI"
    }));

    // Project Number options
    const uniqueProjectNumbers = new Set<string>();
    projectsForCurrentStatus.forEach((q: Questionnaire) => {
      if (q.projectCode) uniqueProjectNumbers.add(q.projectCode);
    });
    
    const projectNumbers: Option[] = Array.from(uniqueProjectNumbers).map(code => ({
      value: code,
      label: code
    }));
    
    // Project Officer options
    const uniqueProjectOfficers = new Set<string>();
    projectsForCurrentStatus.forEach((q: Questionnaire) => {
      if (q.projectOfficer) uniqueProjectOfficers.add(q.projectOfficer);
    });
    
    const projectOfficers: Option[] = Array.from(uniqueProjectOfficers).map(officer => ({
      value: officer,
      label: officer
    }));
    
    // Update states with generated options
    setNameOptions(names);
    setTypeOptions(types);
    setProjectNumberOptions(projectNumbers);
    setProjectOfficerOptions(projectOfficers);
    
    // Réinitialiser les filtres lorsque le statut change
    setFilters({
      names: [],
      types: [],
      projectNumbers: [],
      projectOfficers: []
    });
    
  }, [filteredByStatus]);
  // 2. Advanced filtering
  const filteredData = useMemo(() => {
    return filteredByStatus.filter(project => {
      // Filter by name (using ID as value)
      if (filters.names.length > 0 && !filters.names.includes(project.id)) {
        return false;
      }
      
      // Filter by type (CBE or BBI)
      if (filters.types.length > 0 && !filters.types.includes(project.isCBE.toString())) {
        return false;
      }
      
      // Filter by project number
      if (filters.projectNumbers.length > 0 && 
          (!project.projectCode || !filters.projectNumbers.includes(project.projectCode))) {
        return false;
      }
      
      // Filter by project officer
      if (filters.projectOfficers.length > 0 && 
          (!project.projectOfficer || !filters.projectOfficers.includes(project.projectOfficer))) {
        return false;
      }
      
      return true;
    });
  }, [filteredByStatus, filters]);
  
  // 3. Sorting the filtered data
  const sortedAndFilteredData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    const key = sortConfig.key; // Capture the key to avoid TypeScript issues
    
    return [...filteredData].sort((a, b) => {
      // We need to use any to bypass TypeScript strict restrictions
      // because even with our checks, TS still considers values as potentially undefined
      const valueA: any = a[key] === undefined ? '' : a[key];
      const valueB: any = b[key] === undefined ? '' : b[key];
      
      // Special handling for strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.direction === 'ascending' 
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      
      // Standard comparison for other types (number, boolean)
      if (valueA < valueB) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Function for sorting
  const handleSort = useCallback((key: keyof Questionnaire) => {
    setSortConfig(prevSortConfig => {
      if (prevSortConfig.key === key) {
        if (prevSortConfig.direction === 'ascending') {
          return { key, direction: 'descending' };
        } else if (prevSortConfig.direction === 'descending') {
          return { key: null, direction: null };
        }
      }
      return { key, direction: 'ascending' };
    });
  }, []);

  // Function to get the sort indicator
  const getSortIndicator = useCallback((key: keyof Questionnaire): string => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? '▲' : '▼';
  }, [sortConfig]);

  // Function to handle the export of selected questionnaires
  const handleSelectedQuestionnaireExport = async () => {
    try {
      console.log('questionnaire list : ' , selectedQuestionnaireList)
      await adminApi.getSelectedProjectsExtract(selectedQuestionnaireList);
    } catch (error) {
    }
  };
  
  // NOUVELLE FONCTION: Export des logs pour les projets sélectionnés
  const handleSelectedQuestionnaireLogsExport = async () => {
    try {
      console.log('exporting logs for questionnaire list: ', selectedQuestionnaireList);
      await adminApi.getSelectedLogExtract(selectedQuestionnaireList);
    } catch (error) {
      console.error("Error exporting logs for selected questionnaires:", error);
    }
  };

  // Handlers for actions
  const handleGetPdf = async (questionnaireId: string, questionnaireName: string) => {
    if (!userToken) {
      const b2cResponse = await instance.acquireTokenSilent({
        scopes: [scopeUrl],
        account: accounts[0],
      });
      setuserToken(b2cResponse.accessToken);
    }

    const response = await axios.get(config.apiUrl + "/Project/GetPDF", {
      params: { questionnaireId: questionnaireId },
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    });

    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", questionnaireName + ".pdf");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleGetExtract = async () => {
    if (!userToken) {
      const b2cResponse = await instance.acquireTokenSilent({
        scopes: [scopeUrl],
        account: accounts[0],
      });
      setuserToken(b2cResponse.accessToken);
    }

    try {
      const response = await axios({
        url: config.apiUrl + "/Project/GetDataExtract",
        method: 'GET',
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${userToken}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'DATA_EXTRACT.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleProgressStatusModal = (projectId: string) => {
    setSelectedSelectedProjectId(projectId);
    setModalProgressIsOpen(true);
  };

  const handleProgressStatusApiCall = async () => {
    const result = await api.progressStatus(selectedSelectedProjectId);
    if (result === "success") {
      fetchData();
    }
    setModalProgressIsOpen(false);
  };

  const handleRegressStatusModal = (projectId: string, regressStatusTo: number) => {
    setRegressStatusTo(regressStatusTo);
    setSelectedSelectedProjectId(projectId);
    setModalRegressIsOpen(true);
  };

  const handleRegressStatusApiCall = async (comment: string) => {
    if (regressStatusTo === null) return;
    const result = await api.regressStatus(selectedSelectedProjectId, regressStatusTo, comment);
    if (result === "success") {
      fetchData();
    }
    setModalRegressIsOpen(false);
    setRegressStatusTo(null);
  };
  
  const handleQuestionnaireExport = async () =>{
    var status = selectedStatus? selectedStatus : 1;
    await adminApi.getProjectsExtract(status);
    }

    const handleLastValidatedExport = async () => {
        var status = selectedStatus ? selectedStatus : 1;
        await adminApi.getLastValidatedExtract(status);
    }

  
  const handleCloneQuestionnaireModal = (questionnaireId: string) => {
    setSelectedSelectedProjectId(questionnaireId);
    setModalCloneIsOpen(true);
  };
  
  const handleCloneQuestionnaireApiCall = async () => {
    try {
      await adminApi.cloneQuestionnaire(selectedSelectedProjectId);
      fetchData();
    } catch (error) {
      console.error("Error when cloning questionnaire:", error);
    }
    setModalCloneIsOpen(false);
  };
  
  return (
    <>
      <Header projectName="" loaded={false} isAdmin={isAdmin} />
      {hasHighLevelRole && (
        <div className="d-flex justify-content-center align-items-center">
          <Metroline
            values={statusCounts}
            onPointSelect={setSelectedStatus}
            defaultSelectedPoint={selectedStatus}
          />
        </div>
      )}
      <div className="project-container">
        {isAdmin && (<>
          <div className="button-holder d-flex justify-content-center">
            <input
              className="big-button mt-3 me-3"
              type="submit"
              value="Export Projects"
              onClick={handleQuestionnaireExport}
                      />
                      <input
                          className="big-button mt-3 me-3"
                          type="submit"
                          value="Export Last Approved Data"
                          onClick={handleLastValidatedExport}
                      />
            {selectedQuestionnaireList.length > 0 && (
              <>
                <input
                  className="big-button mt-3 ms-3"
                  type="submit"
                  value={`Export Selected Projects (${selectedQuestionnaireList.length})`}
                  onClick={handleSelectedQuestionnaireExport}
                />
                <input
                  className="big-button mt-3 ms-3"
                  type="submit"
                  value={`Export Selected Logs (${selectedQuestionnaireList.length})`}
                  onClick={handleSelectedQuestionnaireLogsExport}
                />
              </>
            )}
          </div></>
        )}
        <div className="feedstock-container">
          <h1 className="page-title">Your projects</h1>
          
          <QuestionnaireFilter
            onFiltersChange={handleFiltersChange} 
            nameOptions={nameOptions} 
            typeOptions={typeOptions}
            projectNumberOptions={projectNumberOptions}
            projectOfficerOptions={projectOfficerOptions}
          />
          
          {isAdmin && selectedQuestionnaireList.length > 0 && (
            <div className="mt-3 mb-3">
              <p>Selected projects: {selectedQuestionnaireList.length}</p>
            </div>
          )}
          
          <table className="table mt-5">
            <thead>
              <tr>
                {isAdmin && 
                <SortableHeader<Questionnaire>
                  label="Select"
                  sortKey="id"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                  isAction={true}
                />}
                <SortableHeader<Questionnaire>
                  label="Programme"
                  sortKey="isCBE"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Project acronym"
                  sortKey="name"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Project number"
                  sortKey="projectCode"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Project Officer"
                  sortKey="projectOfficer"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Project Coordinator"
                  sortKey="projectCoordinator"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Active user"
                  sortKey="userLockingEmail"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Timestamp"
                  sortKey="lastModifiedDate_User"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="My Role"
                  sortKey="userRole"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                />
                <SortableHeader<Questionnaire>
                  label="Action"
                  sortKey="id"
                  sortData={handleSort}
                  getSortIndicator={getSortIndicator}
                  isAction={true}
                />
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredData.map((elem, index) => (
                <tr key={index}>
                    {isAdmin && (
                  <td>
                      <input 
                        type="checkbox" 
                        checked={selectedQuestionnaireList.includes(elem.id)}
                        onChange={(e) => handleSelectQuestionnaire(elem.id, e.target.checked)}
                      />
                  </td>
                    )}
                  <td>
                    {/* Program (BBI/CBE) */}
                    {elem.isCBE ? "CBE" : "BBI"}
                  </td>
                  <td>
                    {/* Project acronym */}
                    {elem.name?? "-"}
                  </td>
                  <td>
                    {/* Project Number */}
                    {elem.projectCode?? "-"}
                  </td>
                  <td>
                    {/* Project Officer */}
                    {elem.projectOfficer ?? "-"}
                  </td>
                  <td>
                    {/* Project Coordinator */}
                    {elem.projectCoordinator ?? "-"}
                  </td>
                  <td>
                    {/* User editing */}
                    {elem.userLockingEmail ?? "-"}
                  </td>
                  <td>
                    {/* Timestamp */}
                    {elem.lastModifiedDate_User ?? "-"}
                  </td>
                  <td>
                    {/* My role */}
                    {elem.userRole === 1 && "Encoder"}
                    {elem.userRole === 2 && "Validator"}
                    {elem.userRole === 3 && "Project Verificator"}
                    {elem.userRole === 4 && "Project Approver"}
                  </td>
                  <td className="actions">
                    {(elem.userLockingEmail === null || elem.userRole === 3) && (
                      <a
                        className="action-button"
                        href={"/UpdateProject/" + elem.id}
                      >
                        {(elem.userRole === 3 || elem.userRole === 4 || elem.status !== 1) ? (
                          <span>View project</span>
                        ) : (
                          <span>Edit Project</span>
                        )}
                      </a>
                    )}
                    {elem.status === 2 && elem.userRole === 3 && (
                      <>
                        <a
                          className="action-button"
                          onClick={() => handleProgressStatusModal(elem.id)}
                        >
                          <span>Verify</span>
                        </a>
                        <a
                          className="action-button"
                          onClick={() => handleRegressStatusModal(elem.id, 1)}
                        >
                          <span>Reopen</span>
                        </a>
                      </>
                    )}
                    {elem.status === 3 && elem.userRole === 4 && (
                      <>
                        <a
                          className="action-button"
                          onClick={() => handleProgressStatusModal(elem.id)}
                        >
                          <span>Approve</span>
                        </a>
                        <a
                          className="action-button"
                          onClick={() => handleRegressStatusModal(elem.id, 1)}
                        >
                          <span>Reopen</span>
                        </a>
                      </>
                    )}
                    {isAdmin && elem.status==4 ? 
                    <a
                      className="action-button"
                      onClick={() => handleCloneQuestionnaireModal(elem.id)}
                    >
                      <span>Manual reopen</span>
                    </a>
                    : null}
                    {((elem.userRole === 1 || elem.userRole === 2) && elem.status >= 2) ||
                     (elem.userRole === 3 || elem.userRole === 4) ||
                     isAdmin ? (
                      <a
                        className="action-button"
                        onClick={() => handleGetPdf(elem.id, elem.name)}
                      >
                        <span>Download PDF</span>
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={modalRegressIsOpen}
        onRequestClose={() => {
          setModalRegressIsOpen(false);
          setRegressStatusTo(null);
        }}
        onConfirm={handleRegressStatusApiCall}
        title="Confirm"
        message="Are you sure you want to reopen this questionnaire?"
        requireComment={true}
      />
      <ConfirmationModal
        isOpen={modalProgressIsOpen}
        onRequestClose={() => setModalProgressIsOpen(false)}
        onConfirm={handleProgressStatusApiCall}
        title="Confirm"
        message="Are you sure you want to perform this action?"
      />
      <ConfirmationModal
        isOpen={modalCloneIsOpen}
        onRequestClose={() => setModalCloneIsOpen(false)}
        onConfirm={handleCloneQuestionnaireApiCall}
        title="Confirm"
        message="Are you sure you want to clone this questionnaire and create a new version?"
      />
    </>
  );
}

export default ListOfQuestionnaires;